/**
 * Hook para gerenciar o fluxo completo de abertura de arquivos
 * com loading, validação de hash e credenciais SMB
 */

import { useState, useCallback, useRef } from 'react';
import { Arquivo } from '../types/gerArquivos';
import { GerArquivosService } from '../services/gerArquivosService';
import { abrirArquivoService } from '../lib/corel/actions/abrirArquivo';
import type { SmbCredentials } from '../lib/corel/actions/abrirArquivo';
import { WebViewResponse } from '../lib/corel/types/messages';
import { sendToTerminal } from '../lib/terminalLogger';
import { prepareSmbCredentials } from '../utils/smbCredentialsHelper';
import { fromActionError, ErrorCode } from '../lib/errors';
import { requestConfirmation } from '../lib/globalConfirm';
import { getFileOpenTimeoutMessage } from '../lib/corel/timeoutMessages';
import { useNotifications } from '../components/feedback/NotificationProvider';
import { APIError } from '../lib/api';

type AbrirArquivoStage = 'buscando_info' | 'buscando_smb' | 'enviando_comando' | 'aguardando_corel' | 'concluido' | 'erro';

interface UseAbrirArquivoFlowReturn {
  abrirArquivo: (arquivo: Arquivo) => Promise<void>;
  carregando: boolean;
  erro: string | null;
  stage: AbrirArquivoStage;
}

// Constantes para timeout e retry
const TIMEOUT_MS = 1200000; // 20 minutos (1.200.000ms)
const MAX_RETRIES = 2; // Máximo de tentativas para erros temporários
const RETRY_DELAY_MS = 1000; // 1 segundo entre tentativas

// Códigos de erro que podem ser retentados automaticamente
const RETRYABLE_ERROR_CODES = [
  ErrorCode.TIMEOUT,
  ErrorCode.API_UNAVAILABLE,
  ErrorCode.SMB_CONEXAO_FALHOU,
  ErrorCode.IO_ACESSO_NEGADO
];

// Mapeamento de mensagens de erro mais descritivas
const ERROR_MESSAGES: Record<string, string> = {
  'OPEN_ERROR': 'Caminho do arquivo não encontrado na resposta da API',
  'Falha ao enviar comando de abertura': 'WebView indisponível ou não inicializado',
  'FILE_NOT_FOUND': 'Arquivo não encontrado no servidor',
  'NETWORK_ERROR': 'Erro de conexão com o servidor de rede',
  'PERMISSION_DENIED': 'Permissão negada para acessar o arquivo',
  'HASH_MISMATCH': 'Arquivo corrompido ou alterado (hash não confere)',
  'SMB_AUTENTICACAO_FALHOU': 'Falha na autenticação com o servidor SMB',
  'SMB_CONEXAO_FALHOU': 'Não foi possível conectar ao servidor de rede',
  'PATH_INACCESSIBLE': 'Caminho do arquivo não está acessível',
  'TIMEOUT': 'Tempo limite excedido ao aguardar resposta do CorelDRAW',
  'API_UNAVAILABLE': 'Servidor API indisponível temporariamente',
  'OPEN_BLOCKED': 'Abertura bloqueada para este arquivo'
};

interface ArquivoOpenResponse {
  caminho: string;
  nome: string;
  hashArquivo?: string;
  smb?: {
    username: string;
    password: string;
    domain?: string | null;
  };
}

export function useAbrirArquivoFlow(
  onSuccess?: (arquivo: Arquivo) => void,
  onError?: (arquivo: Arquivo, erro: string) => void
): UseAbrirArquivoFlowReturn {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [stage, setStage] = useState<AbrirArquivoStage>('buscando_info');
  const { notify } = useNotifications();
  
  // Refs para gerenciar timeout e retry
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const currentArquivoRef = useRef<Arquivo | null>(null);

  // Função auxiliar para limpar timeout
  const clearTimeoutSafe = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Função auxiliar para obter mensagem de erro descritiva
  const getErrorMessage = useCallback((error: any, defaultMsg: string): string => {
    // Se for uma resposta do WebView com código de erro
    if (error && typeof error === 'object') {
      try {
        const appError = fromActionError(error);
        const code = appError.code as string;
        if (ERROR_MESSAGES[code]) {
          return ERROR_MESSAGES[code];
        }
        if (appError.message && appError.message !== 'Falha na ação') {
          return appError.message;
        }
      } catch {
        // Se fromActionError falhar, continuar com lógica abaixo
      }
    }

    // Se for uma string de erro conhecida
    const errorString = error instanceof Error ? error.message : String(error);
    if (ERROR_MESSAGES[errorString]) {
      return ERROR_MESSAGES[errorString];
    }

    // Verificar padrões comuns
    if (/timeout|tempo.*limite/i.test(errorString)) {
      return ERROR_MESSAGES['TIMEOUT'] || 'Tempo limite excedido';
    }
    if (/not found|não encontrado/i.test(errorString)) {
      return ERROR_MESSAGES['FILE_NOT_FOUND'] || 'Arquivo não encontrado';
    }
    if (/permission|permissão|acesso negado/i.test(errorString)) {
      return ERROR_MESSAGES['PERMISSION_DENIED'] || 'Permissão negada';
    }
    if (/hash|integridade/i.test(errorString)) {
      return ERROR_MESSAGES['HASH_MISMATCH'] || 'Falha na validação de integridade';
    }
    if (/network|rede|conexão/i.test(errorString)) {
      return ERROR_MESSAGES['NETWORK_ERROR'] || 'Erro de conexão';
    }

    return defaultMsg;
  }, []);

  const abrirArquivo = useCallback(async (arquivo: Arquivo, retryAttempt = 0): Promise<void> => {
    // Limpar timeout anterior se existir
    clearTimeoutSafe();
    
    setCarregando(true);
    setErro(null);
    setStage('buscando_info');
    currentArquivoRef.current = arquivo;

    if (arquivo.acoes && !arquivo.acoes.podeAbrir) {
      const blockedMsg =
        arquivo.acoes.motivoBloqueio ?? 'Abertura não permitida para este arquivo';
      setCarregando(false);
      setStage('erro');
      setErro(blockedMsg);
      notify({ type: 'warning', message: blockedMsg });
      onError?.(arquivo, blockedMsg);
      return;
    }

    try {
      sendToTerminal('GERARQ_FLOW:ABRIR_INIT', { 
        id: arquivo.id, 
        nome: arquivo.arquivoNome,
        caminho: arquivo.arquivoCaminho 
      });

      // 1. Buscar informações do arquivo na API
      const response = await GerArquivosService.abrirArquivo(arquivo.id, arquivo.source);
      const arquivoInfo = response as unknown as ArquivoOpenResponse;

      sendToTerminal('GERARQ_FLOW:ABRIR_API_OK', { 
        id: arquivo.id, 
        caminho: arquivoInfo.caminho,
        temHash: !!arquivoInfo.hashArquivo
      });

      // 2. Buscar credenciais SMB usando função centralizada
      setStage('buscando_smb');
      sendToTerminal('GERARQ_FLOW:ABRIR_SMB_REQUEST', { id: arquivo.id, credentialId: 3 });
      
      // ✅ Usar função centralizada para preparar credenciais SMB
      const smbCredentials = await prepareSmbCredentials({
        source: 'credential',
        credentialId: 3,
        path: arquivoInfo.caminho,
        context: 'ABRIR-ARQUIVO'
      });
      
      // Se credenciais não foram obtidas, continuar sem SMB (pode ser caminho local)
      // Mas se caminho é UNC e não temos SMB, isso pode causar erro - mas deixamos o Corel lidar
      if (!smbCredentials) {
        sendToTerminal('⚠️ [ABRIR-ARQUIVO:SMB] Credenciais não obtidas - continuando sem SMB', {
          id: arquivo.id,
          caminho: arquivoInfo.caminho,
          isUnc: arquivoInfo.caminho?.startsWith('\\\\') || arquivoInfo.caminho?.startsWith('//')
        });
      } else {
        sendToTerminal('✅ [ABRIR-ARQUIVO:SMB] Credenciais preparadas', {
          id: arquivo.id,
          username: smbCredentials.username,
          hasDomain: 'domain' in smbCredentials,
          domainValue: smbCredentials.domain || null
        });
      }
      
      // Converter para formato esperado pelo abrirArquivoService (SmbCredentials)
      const smbForService: SmbCredentials | undefined = smbCredentials ? {
        username: smbCredentials.username,
        password: smbCredentials.password,
        ...(smbCredentials.domain ? { domain: smbCredentials.domain } : {})
      } : undefined;

      // 3. Preparar payload baseado na presença de hash
      setStage('enviando_comando');
      const temHash = !!arquivoInfo.hashArquivo && arquivoInfo.hashArquivo.trim() !== '';

      // Callback compartilhado para tratar respostas
      const handleResponse = (response: WebViewResponse) => {
        // Limpar timeout quando receber resposta
        clearTimeoutSafe();
        
        if (response.status === 'ok') {
          retryCountRef.current = 0; // Reset retry count em caso de sucesso
          setStage('concluido');
          setTimeout(() => {
            setCarregando(false);
            currentArquivoRef.current = null;
            onSuccess?.(arquivo);
          }, 500); // Pequeno delay para mostrar sucesso
          sendToTerminal('GERARQ_FLOW:ABRIR_SUCCESS', { 
            id: arquivo.id,
            requestId: response.requestId 
          });
        } else {
          // Tentar obter mensagem de erro mais descritiva
          const errorResponse = response.result || response;
          const errorMsg = getErrorMessage(errorResponse, response.message || 'Erro ao abrir arquivo');
          
          // Verificar se é um erro retentável e se ainda temos tentativas
          let errorCode: ErrorCode | string | undefined;
          try {
            const appError = fromActionError(errorResponse);
            errorCode = appError.code;
          } catch {
            // Se não conseguir mapear, usar código padrão
            errorCode = ErrorCode.UNKNOWN;
          }

          const isRetryable = errorCode && RETRYABLE_ERROR_CODES.includes(errorCode as ErrorCode);
          const canRetry = retryAttempt < MAX_RETRIES && isRetryable;

          if (canRetry) {
            // Aguardar um pouco antes de tentar novamente
            sendToTerminal('GERARQ_FLOW:ABRIR_RETRY', { 
              id: arquivo.id,
              attempt: retryAttempt + 1,
              maxRetries: MAX_RETRIES,
              errorCode
            });
            
            setTimeout(() => {
              abrirArquivo(arquivo, retryAttempt + 1);
            }, RETRY_DELAY_MS);
            return; // Não marcar como erro ainda, vamos tentar novamente
          }

          // Se não for retentável ou esgotamos tentativas, marcar como erro
          setCarregando(false);
          setStage('erro');
          setErro(errorMsg);
          retryCountRef.current = 0; // Reset para próxima tentativa
          currentArquivoRef.current = null; // Limpar ref quando finalizar
          sendToTerminal('GERARQ_FLOW:ABRIR_ERROR', { 
            id: arquivo.id,
            status: response.status,
            message: errorMsg,
            errorCode,
            retryAttempt,
            willRetry: false
          });
          onError?.(arquivo, errorMsg);
        }
      };

      // Marcar que estamos aguardando resposta do Corel
      setStage('aguardando_corel');

      // Configurar timeout para abertura de arquivo (20 minutos)
      // ✅ CORRIGIDO: Timeout só expira se a resposta NÃO chegar em 20 minutos
      const arquivoIdAtTimeout = arquivo.id;
      
      // Função recursiva para permitir múltiplos timeouts com confirmação
      const setupTimeout = (timeoutCount: number = 1) => {
        const totalMinutes = timeoutCount * 20;
        
        timeoutRef.current = setTimeout(() => {
          // Verificar se ainda estamos aguardando o mesmo arquivo
          if (currentArquivoRef.current?.id === arquivoIdAtTimeout) {
            sendToTerminal('⏰ [ABRIR-ARQUIVO:TIMEOUT] Timeout expirado', { 
              id: arquivoIdAtTimeout,
              timeoutMs: TIMEOUT_MS,
              timeoutCount,
              totalMinutes,
              retryAttempt 
            });
            
            // Usar setTimeout para não bloquear o callback do WebView
            setTimeout(async () => {
              // Verificar novamente se ainda está aguardando (callback pode ter chegado durante o setTimeout)
              if (currentArquivoRef.current?.id === arquivoIdAtTimeout) {
                // Obter mensagem de timeout formatada
                const messageData = getFileOpenTimeoutMessage(totalMinutes, 20);

                // Usar sistema de confirmação centralizado
                const continuar = await requestConfirmation({
                  title: messageData.title,
                  message: messageData.message,
                  confirmLabel: messageData.confirmLabel,
                  cancelLabel: messageData.cancelLabel
                });
                
                if (continuar) {
                  sendToTerminal('✅ [ABRIR-ARQUIVO:TIMEOUT] Usuário escolheu continuar', {
                    id: arquivoIdAtTimeout,
                    timeoutCount: timeoutCount + 1,
                    totalMinutes: totalMinutes + 20
                  });
                  
                  // Recursivamente criar novo timeout
                  setupTimeout(timeoutCount + 1);
                } else {
                  // Usuário cancelou
                  clearTimeoutSafe();
                  setCarregando(false);
                  setStage('erro');
                  currentArquivoRef.current = null;
                  const cancelMsg = `Operação cancelada pelo usuário após ${totalMinutes} minutos de espera`;
                  setErro(cancelMsg);
                  
                  sendToTerminal('❌ [ABRIR-ARQUIVO:TIMEOUT] Usuário cancelou', { 
                    id: arquivoIdAtTimeout,
                    totalMinutes 
                  });
                  
                  onError?.(arquivo, cancelMsg);
                }
              } else {
                sendToTerminal('✅ [ABRIR-ARQUIVO:TIMEOUT] Resposta chegou durante confirmação', {
                  id: arquivoIdAtTimeout
                });
              }
            }, 0);
          }
        }, TIMEOUT_MS);
      };
      
      // Iniciar primeiro timeout
      setupTimeout(1);

      if (temHash) {
        // Fluxo com validação de integridade
        sendToTerminal('GERARQ_FLOW:ABRIR_COM_HASH', { 
          id: arquivo.id,
          hash: arquivoInfo.hashArquivo 
        });

        // Construir payload completo para log
        const payloadComHash: any = {
          action: 'abrir-arquivo',
          data: {
            caminhoArquivo: arquivoInfo.caminho,
            validarIntegridade: true,
            hashEsperado: arquivoInfo.hashArquivo,
            smb: smbForService ? {
              username: smbForService.username,
              password: '[REDACTED]', // Não logar senha
              ...(smbForService.domain ? { domain: smbForService.domain } : {})
            } : undefined,
            opcoes: {
              verificarConectividadeRede: true
            }
          }
        };

        // Log do payload completo antes de enviar
        sendToTerminal('GERARQ_FLOW:PAYLOAD_ACTION', {
          requestId: `req-abrir-${Date.now()}`,
          payload: payloadComHash,
          payloadRaw: JSON.stringify({
            action: 'abrir-arquivo',
            data: {
              caminhoArquivo: arquivoInfo.caminho,
            validarIntegridade: true,
            hashEsperado: arquivoInfo.hashArquivo,
            smb: smbForService ? {
              username: smbForService.username,
              password: '[REDACTED]',
              ...(smbForService.domain ? { domain: smbForService.domain } : {})
            } : undefined,
              opcoes: {
                verificarConectividadeRede: true
              }
            }
          }, null, 2)
        });

        const requestId = abrirArquivoService.abrirArquivoComValidacao(
          arquivoInfo.caminho,
          {
            hashEsperado: arquivoInfo.hashArquivo,
            validarIntegridade: true,
            smb: smbForService,
            opcoes: {
              verificarConectividadeRede: true
            }
          },
          handleResponse
        );

        if (!requestId) {
          throw new Error('Falha ao enviar comando de abertura');
        }
      } else {
        // Fluxo sem hash (abertura simples)
        sendToTerminal('GERARQ_FLOW:ABRIR_SEM_HASH', { id: arquivo.id });

        // Construir payload completo para log
        const payloadSemHash: any = {
          action: 'abrir-arquivo',
          data: {
            caminhoArquivo: arquivoInfo.caminho,
            smb: smbForService ? {
              username: smbForService.username,
              password: '[REDACTED]', // Não logar senha
              ...(smbForService.domain ? { domain: smbForService.domain } : {})
            } : undefined,
            opcoes: {
              verificarConectividadeRede: true
            }
          }
        };

        // Log do payload completo antes de enviar
        sendToTerminal('GERARQ_FLOW:PAYLOAD_ACTION', {
          requestId: `req-abrir-${Date.now()}`,
          payload: payloadSemHash,
          payloadRaw: JSON.stringify({
            action: 'abrir-arquivo',
            data: {
              caminhoArquivo: arquivoInfo.caminho,
              smb: smbForService ? {
                username: smbForService.username,
                password: '[REDACTED]',
                ...(smbForService.domain ? { domain: smbForService.domain } : {})
              } : undefined,
              opcoes: {
                verificarConectividadeRede: true
              }
            }
          }, null, 2)
        });

        const requestId = abrirArquivoService.abrirArquivoComOpcoes(
          arquivoInfo.caminho,
          {
            smb: smbForService,
            verificarConectividadeRede: true
          },
          handleResponse
        );

        if (!requestId) {
          throw new Error('Falha ao enviar comando de abertura');
        }
      }
    } catch (error) {
      clearTimeoutSafe();
      setCarregando(false);
      setStage('erro');

      const is403 =
        (error instanceof APIError && error.status === 403) ||
        (error instanceof Error &&
          (error as Error & { status?: number }).status === 403);

      let errorMsg = getErrorMessage(error, 'Erro desconhecido ao abrir arquivo');
      if (is403) {
        errorMsg =
          error instanceof Error
            ? error.message
            : 'Abertura bloqueada para este arquivo';
        notify({ type: 'warning', message: errorMsg });
      }
      setErro(errorMsg);
      
      // Verificar se é um erro retentável
      let errorCode: ErrorCode | string | undefined;
      const isNetworkError = error instanceof TypeError && error.message === 'Failed to fetch';
      const isApiError = error && typeof error === 'object' && 'status' in error;
      
      if (isNetworkError) {
        errorCode = ErrorCode.API_UNAVAILABLE;
      } else if (isApiError && (error as any).status >= 500) {
        errorCode = ErrorCode.API_5XX;
      } else if (isApiError && (error as any).status === 0) {
        errorCode = ErrorCode.API_UNAVAILABLE;
      }

      const isRetryable = errorCode && RETRYABLE_ERROR_CODES.includes(errorCode as ErrorCode);
      const canRetry = retryAttempt < MAX_RETRIES && isRetryable;

      if (canRetry) {
        sendToTerminal('GERARQ_FLOW:ABRIR_EXCEPTION_RETRY', { 
          id: arquivo.id,
          error: errorMsg,
          errorCode,
          attempt: retryAttempt + 1,
          maxRetries: MAX_RETRIES
        });
        
        setTimeout(() => {
          abrirArquivo(arquivo, retryAttempt + 1);
        }, RETRY_DELAY_MS);
        return;
      }

      retryCountRef.current = 0; // Reset para próxima tentativa
      currentArquivoRef.current = null; // Limpar ref quando finalizar (erro não retentável)
      
      sendToTerminal('GERARQ_FLOW:ABRIR_EXCEPTION', { 
        id: arquivo.id,
        error: errorMsg,
        errorCode,
        retryAttempt,
        willRetry: false
      });
      
      onError?.(arquivo, errorMsg);
    }
  }, [onSuccess, onError, clearTimeoutSafe, getErrorMessage, notify]);

  return {
    abrirArquivo,
    carregando,
    erro,
    stage
  };
}

